import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../contexts/AdminContext';
import { PencilIcon, TrashIcon, PlusIcon, EyeIcon } from '@heroicons/react/24/outline';

const BlogManager = () => {
  const { blogPosts: savedPosts, addBlogPost, updateBlogPost, deleteBlogPost } = useAdmin();
  const [posts, setPosts] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // Initialize posts from saved data or use default
  useEffect(() => {
    if (savedPosts) {
      setPosts(savedPosts);
    } else {
      // Use original blog posts as fallback
      setPosts([]);
    }
  }, [savedPosts]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    keywords: '',
    author: '',
    category: '',
    tags: '',
    image: '',
    content: ''
  });

  const categories = ['Business Registration', 'GST Compliance', 'NRI Services', 'Tax Planning', 'Financial Advisory', 'Company News'];

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const postData = {
      ...formData,
      tags: formData.tags.split(',').map(tag => tag.trim()),
      keywords: formData.keywords.split(',').map(keyword => keyword.trim()),
      date: new Date().toISOString().split('T')[0],
      readTime: Math.ceil(formData.content.split(' ').length / 200) + ' min read'
    };

    if (editingPost) {
      updateBlogPost(editingPost.id, postData);
    } else {
      addBlogPost(postData);
    }

    resetForm();
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      keywords: '',
      author: '',
      category: '',
      tags: '',
      image: '',
      content: ''
    });
    setIsEditing(false);
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      description: post.description,
      keywords: post.keywords?.join(', ') || '',
      author: post.author,
      category: post.category,
      tags: post.tags?.join(', ') || '',
      image: post.image || '',
      content: post.content || ''
    });
    setShowForm(true);
    setIsEditing(true);
  };

  const handleDelete = (post) => {
    if (window.confirm(`Are you sure you want to delete "${post.title}"? This cannot be undone.`)) {
      deleteBlogPost(post.id);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="section-padding">
      <div className="container-custom">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold text-naqd-light-text dark:text-naqd-dark-text mb-2">
                Blog Manager
              </h1>
              <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                Create, edit, and manage your blog posts
              </p>
            </div>
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-naqd-primary text-white rounded-lg hover:bg-naqd-primary/90 transition-colors flex items-center"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              New Post
            </button>
          </div>

          {/* Blog Posts List */}
          {!showForm ? (
            <div className="space-y-4">
              {posts.length === 0 ? (
                <div className="card text-center py-12">
                  <h3 className="text-xl font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-2">
                    No blog posts yet
                  </h3>
                  <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-4">
                    Create your first blog post to get started
                  </p>
                  <button
                    onClick={() => setShowForm(true)}
                    className="px-4 py-2 bg-naqd-primary text-white rounded-lg hover:bg-naqd-primary/90 transition-colors"
                  >
                    Create First Post
                  </button>
                </div>
              ) : (
                posts.map(post => (
                  <div key={post.id} className="card">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-naqd-light-text dark:text-naqd-dark-text mb-2">
                          {post.title}
                        </h3>
                        <p className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                          {post.description}
                        </p>
                        <div className="flex items-center space-x-4 text-sm text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary">
                          <span>By {post.author}</span>
                          <span>•</span>
                          <span>{post.date}</span>
                          <span>•</span>
                          <span>{post.category}</span>
                          <span>•</span>
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(post)}
                          className="p-2 text-blue-600 hover:text-blue-800 transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <a
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-green-600 hover:text-green-800 transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </a>
                        <button
                          onClick={() => handleDelete(post)}
                          className="p-2 text-red-600 hover:text-red-800 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Blog Post Form */
            <div className="card">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-naqd-light-text dark:text-naqd-dark-text">
                  {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary hover:text-naqd-light-text dark:hover:text-naqd-dark-text"
                >
                  Close
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                      Title *
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                      Author *
                    </label>
                    <input
                      type="text"
                      name="author"
                      value={formData.author}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                      Category *
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                    >
                      <option value="">Select category...</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                      Image URL
                    </label>
                    <input
                      type="url"
                      name="image"
                      value={formData.image}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg"
                      className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    required
                    rows="3"
                    className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                    placeholder="Brief description for SEO..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                    placeholder="keyword1, keyword2, keyword3..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text"
                    placeholder="tag1, tag2, tag3..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-naqd-light-text-secondary dark:text-naqd-dark-text-secondary mb-2">
                    Content *
                  </label>
                  <textarea
                    name="content"
                    value={formData.content}
                    onChange={handleChange}
                    required
                    rows="20"
                    className="w-full px-3 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg focus:outline-none focus:ring-2 focus:ring-naqd-primary dark:focus:ring-naqd-green bg-white dark:bg-naqd-dark-surface text-naqd-light-text dark:text-naqd-dark-text font-mono"
                    placeholder="Write your blog content here... Use markdown-style formatting with # for headings, ** for bold, etc."
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-naqd-primary text-white rounded-lg hover:bg-naqd-primary/90 transition-colors"
                  >
                    {isEditing ? 'Update Post' : 'Create Post'}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-naqd-light-border dark:border-naqd-dark-border rounded-lg hover:bg-naqd-light-surface dark:hover:bg-naqd-dark-surface transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogManager;